/**
 * PaymentMethodsConfig.jsx — Finance → Payment Methods Administration Center
 *
 * Full admin UI for all six client-facing payment methods.
 * Canonical source: payment_method_configs collection, governed by payment_methods.py.
 * Does NOT create a parallel config system.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import * as Icons from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FinanceTabs, FinanceHeader } from "./financeShell";
import { toast } from "sonner";

// ── Canonical client-facing codes and display data (P20) ─────────────────────
const METHOD_DISPLAY = {
  stripe:    { label: "Credit / Debit Card",          icon: "CreditCard",     color: "bg-violet-100 text-violet-700", integration: "API Checkout",     verification: "Automatic"  },
  paypal:    { label: "PayPal",                        icon: "Wallet",         color: "bg-blue-100 text-blue-700",    integration: "API Checkout",     verification: "Automatic"  },
  wise:      { label: "Wise",                          icon: "ArrowRightLeft", color: "bg-emerald-100 text-emerald-700", integration: "External Transfer", verification: "Manual"   },
  thai_bank: { label: "Thai Direct Bank Transfer",     icon: "Building2",      color: "bg-amber-100 text-amber-700",  integration: "External Transfer", verification: "Manual"   },
  thai_qr:   { label: "Thai QR Code",                  icon: "QrCode",         color: "bg-teal-100 text-teal-700",   integration: "Static QR",        verification: "Manual"     },
  wire:      { label: "International Wire Transfer",   icon: "Globe",          color: "bg-slate-100 text-slate-700", integration: "External Wire",     verification: "Manual"     },
};

const CANONICAL_ORDER = ["stripe", "paypal", "wise", "thai_bank", "thai_qr", "wire"];

const CURRENCIES = ["USD", "EUR", "GBP", "THB", "AUD", "CAD", "SGD", "CHF", "JPY", "NZD", "HKD", "MYR"];

const inputCls  = "w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30 focus:border-[#F5A623]/60 bg-white";
const monoCls   = `${inputCls} font-mono`;
const textaCls  = `${inputCls} resize-none`;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(v, ccy = "USD") {
  try { return new Intl.NumberFormat("en-US", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(Number(v || 0)); }
  catch { return `${ccy} ${Number(v || 0).toFixed(2)}`; }
}
function mask(val) {
  if (!val) return null;
  const s = String(val).trim();
  return s.length <= 4 ? "••••" : "••••" + s.slice(-4);
}

// ── Shared sub-components ─────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={onChange} disabled={disabled}
      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none
        focus-visible:ring-2 focus-visible:ring-[#F5A623] disabled:cursor-not-allowed disabled:opacity-40
        ${checked ? "bg-emerald-500" : "bg-slate-200"}`}>
      <span className={`h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform
        ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

function CurrencyPicker({ value = [], onChange }) {
  const toggle = c => onChange(value.includes(c) ? value.filter(x => x !== c) : [...value, c]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="currency-picker">
      {CURRENCIES.map(c => (
        <button key={c} type="button" onClick={() => toggle(c)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors select-none
            ${value.includes(c) ? "bg-[#171B2E] text-white border-[#171B2E]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
          data-testid={`currency-chip-${c}`}>{c}
        </button>
      ))}
    </div>
  );
}

function FieldLabel({ label, required, note, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">
        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
      </label>
      {children}
      {note && <p className="text-xs text-slate-400 mt-1 leading-relaxed">{note}</p>}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="col-span-full border-t border-slate-100 pt-4 first:border-0 first:pt-0">
      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">{children}</p>
    </div>
  );
}

function InfoBox({ icon: Ic = Icons.Info, color = "violet", title, children }) {
  const map = { violet: "bg-violet-50 border-violet-200 text-violet-800", blue: "bg-blue-50 border-blue-200 text-blue-800", amber: "bg-amber-50 border-amber-200 text-amber-800", rose: "bg-rose-50 border-rose-200 text-rose-700", slate: "bg-slate-50 border-slate-200 text-slate-700" };
  return (
    <div className={`rounded-xl border p-4 ${map[color] || map.slate}`}>
      <div className="flex items-start gap-2.5">
        <Ic className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          {title && <p className="font-semibold text-sm mb-1">{title}</p>}
          <div className="text-sm leading-relaxed">{children}</div>
        </div>
      </div>
    </div>
  );
}

// QR image uploader
function QRUploader({ value, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleFile = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/gif", "image/webp"].includes(file.type)) {
      toast.error("Please upload a PNG, JPEG, GIF, or WebP image");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "qr_code");
      fd.append("entity_type", "payment_config");
      const { data } = await api.post("/files/upload", fd);
      if (!data.id) throw new Error("No document ID returned");
      onChange(data.id);
      toast.success("QR image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Upload failed");
    } finally { setUploading(false); if (e.target) e.target.value = ""; }
  };
  const previewSrc = value ? `${API}/files/${value}/download?auth=${getToken()}` : null;
  return (
    <div className="space-y-3">
      {previewSrc && (
        <div className="relative inline-block">
          <img src={previewSrc} alt="QR Code Preview"
            className="h-36 w-36 rounded-xl border border-slate-200 object-contain bg-white shadow-sm"
            onError={e => { e.target.style.display = "none"; }} />
          <div className="absolute -top-1.5 -right-1.5 bg-emerald-500 rounded-full p-0.5">
            <Icons.Check className="h-3 w-3 text-white" />
          </div>
        </div>
      )}
      <div className="flex items-center gap-3">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={handleFile} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          data-testid="qr-upload-btn">
          {uploading ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Upload className="h-4 w-4" />}
          {value ? "Replace QR Image" : "Upload QR Image"}
        </button>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs text-rose-500 hover:text-rose-700">Remove</button>}
      </div>
      {!value && <p className="text-xs text-slate-400">Upload an approved PromptPay or bank-issued QR code. Served only to authenticated portal clients.</p>}
    </div>
  );
}

// Client preview panel (P13)
function ClientPreview({ method, previewRef = "INV-000123", previewAmount = 1250 }) {
  const code = method.method_code;
  const ccy = (method.supported_currencies || ["USD"])[0] || "USD";
  const ref = (method.payment_reference_format || "INV-{invoice_number}").replace("{invoice_number}", previewRef.replace("INV-", ""));

  if (code === "stripe" || code === "paypal") return null;

  return (
    <div className="bg-slate-900 rounded-xl p-4 text-white text-sm" data-testid={`client-preview-${code}`}>
      <p className="text-xs text-slate-400 uppercase tracking-widest mb-3 font-semibold">Client Preview</p>
      <p className="font-semibold text-base mb-3">{METHOD_DISPLAY[code]?.label}</p>

      {method.instructions && (
        <p className="text-slate-300 text-xs mb-3 leading-relaxed">{method.instructions}</p>
      )}

      <div className="bg-slate-800 rounded-lg p-3 space-y-2 mb-3">
        {code === "wise" && (() => {
          const wd = method.wise_details || {};
          const tagOk  = wd.wise_tag_enabled  && wd.wise_tag;
          const linkOk = wd.payment_link_enabled && wd.payment_link;
          const qrOk   = wd.qr_enabled && wd.qr_document_id;
          const anyOption = tagOk || linkOk || qrOk;
          return (<>
            <div className="text-xs text-slate-300 mb-2">Amount Due: <span className="font-bold text-white">{fmt(previewAmount, ccy)}</span></div>
            {!anyOption && <p className="text-xs text-amber-400">Configure at least one Wise payment option to show a preview.</p>}
            {tagOk && (
              <div className="bg-slate-700 rounded-lg p-2">
                <div className="text-xs text-slate-400 mb-1">Wise Tag</div>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-emerald-300 text-sm">{wd.wise_tag}</span>
                  <Icons.Copy className="h-3 w-3 text-slate-400" />
                </div>
              </div>
            )}
            {linkOk && (
              <div className="bg-slate-700 rounded-lg p-2">
                <div className="text-xs text-slate-400 mb-1">Wise Payment Link</div>
                <div className="flex items-center gap-2 text-xs text-blue-300 break-all">{wd.payment_link.replace("https://","")}</div>
                <div className="mt-2 bg-emerald-500 text-white text-xs text-center rounded py-1 font-semibold">Pay with Wise</div>
              </div>
            )}
            {qrOk && (
              <div className="bg-slate-700 rounded-lg p-2">
                <div className="text-xs text-slate-400 mb-1">Wise QR Code</div>
                <div className="h-20 w-20 mx-auto bg-slate-600 rounded-lg flex items-center justify-center">
                  <Icons.QrCode className="h-10 w-10 text-slate-400" />
                </div>
              </div>
            )}
          </>);
        })()}
        {(code === "thai_bank" || code === "wire") && method.bank_details && Object.entries(method.bank_details).map(([k, v]) =>
          v ? <div key={k} className="flex justify-between text-xs">
            <span className="text-slate-400 capitalize">{k.replace(/_/g, " ")}</span>
            <span className="font-mono text-slate-200">{v}</span>
          </div> : null
        )}
        {code === "thai_qr" && (
          method.qr_document_id
            ? <img src={`${API}/portal/payment-config/qr-image?method_code=thai_qr`} alt="QR" className="h-28 w-28 mx-auto rounded-lg bg-white p-1" />
            : <div className="h-28 w-28 mx-auto bg-slate-700 rounded-lg flex items-center justify-center"><Icons.QrCode className="h-10 w-10 text-slate-500" /></div>
        )}
        <div className="flex justify-between text-xs border-t border-slate-700 pt-2 mt-1">
          <span className="text-slate-400">Payment Reference</span>
          <span className="font-mono text-amber-400">{ref}</span>
        </div>
        <div className="flex justify-between text-xs">
          <span className="text-slate-400">Amount Due</span>
          <span className="font-semibold text-white">{fmt(previewAmount, ccy)}</span>
        </div>
      </div>
      <p className="text-xs text-slate-400">After sending, the client submits a payment report. Staff verifies and posts via Verify &amp; Post.</p>
    </div>
  );
}

// ── Configuration form (inside modal) ────────────────────────────────────────
function MethodConfigForm({ method, stripeConfigured, paypalApiConfigured, onSave, onClose }) {
  const code = method.method_code;
  const [form, setForm] = useState({
    display_name:             method.display_name || "",
    subtitle:                 method.subtitle || "",
    order:                    method.order ?? "",
    supported_currencies:     method.supported_currencies || [],
    instructions:             method.instructions || "",
    payment_reference_format: method.payment_reference_format || "INV-{invoice_number}",
    paypal_email:             method.paypal_email || "",
    fee_pct:                  method.fee_pct ?? 4.0,
    qr_document_id:           method.qr_document_id || "",
    wise_details: {
      wise_tag:           method.wise_details?.wise_tag || "",
      wise_tag_enabled:   method.wise_details?.wise_tag_enabled ?? true,
      payment_link:       method.wise_details?.payment_link || "",
      payment_link_enabled: method.wise_details?.payment_link_enabled ?? true,
      qr_document_id:     method.wise_details?.qr_document_id || "",
      qr_enabled:         method.wise_details?.qr_enabled ?? false,
    },
    bank_details: {
      beneficiary_name:    method.bank_details?.beneficiary_name || "",
      beneficiary_address: method.bank_details?.beneficiary_address || "",
      bank_name:           method.bank_details?.bank_name || "",
      bank_address:        method.bank_details?.bank_address || "",
      account_name:        method.bank_details?.account_name || "",
      account_number:      method.bank_details?.account_number || "",
      phone_number:        method.bank_details?.phone_number || method.bank_details?.mobile_number || method.bank_details?.mobile || method.bank_details?.phone || "",
      branch:              method.bank_details?.branch || "",
      swift_bic:           method.bank_details?.swift_bic || "",
      iban:                method.bank_details?.iban || "",
      routing_number:      method.bank_details?.routing_number || "",
      correspondent_bank:  method.bank_details?.correspondent_bank || "",
      correspondent_swift: method.bank_details?.correspondent_swift || "",
      wire_instructions:   method.bank_details?.wire_instructions || "",
    },
  });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const setF = (path, val) => setForm(prev => {
    const parts = path.split(".");
    if (parts.length === 1) return { ...prev, [path]: val };
    const [parent, child] = parts;
    return { ...prev, [parent]: { ...(prev[parent] || {}), [child]: val } };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        method_code:              code,
        display_name:             form.display_name,
        subtitle:                 form.subtitle,
        order:                    form.order !== "" ? Number(form.order) : undefined,
        supported_currencies:     form.supported_currencies,
        instructions:             form.instructions,
        payment_reference_format: form.payment_reference_format,
        ...(code === "paypal" && { paypal_email: form.paypal_email, fee_pct: Number(form.fee_pct) }),
        ...(code === "thai_qr"   && { qr_document_id: form.qr_document_id }),
        ...(code === "wise"      && { wise_details: form.wise_details }),
        ...((code === "thai_bank" || code === "wire") && { bank_details: form.bank_details }),
      };
      await api.post("/finance/payment-methods", payload);
      toast.success(`${form.display_name || code} configuration saved`);
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const previewMethod = { ...method, ...form };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid={`pm-form-${code}`}>
      {/* Modal header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-3">
          {(() => {
            const m = METHOD_DISPLAY[code] || {};
            const Ic = Icons[m.icon || "CreditCard"];
            return (
              <>
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${m.color || "bg-slate-100 text-slate-700"}`}>
                  <Ic className="h-4.5 w-4.5" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 text-sm">{form.display_name || METHOD_DISPLAY[code]?.label}</h3>
                  <p className="text-xs text-slate-400">{METHOD_DISPLAY[code]?.integration} · {METHOD_DISPLAY[code]?.verification}</p>
                </div>
              </>
            );
          })()}
        </div>
        <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid="pm-modal-close">
          <Icons.X className="h-4 w-4" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

        {/* ── Common display fields ── */}
        <div>
          <SectionLabel>Display &amp; Ordering</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FieldLabel label="Display Name">
              <input value={form.display_name} onChange={e => setF("display_name", e.target.value)}
                className={inputCls} placeholder="Credit / Debit Card"
                data-testid={`pm-field-display-name-${code}`} />
            </FieldLabel>
            <FieldLabel label="Subtitle" note="Short description shown to clients">
              <input value={form.subtitle} onChange={e => setF("subtitle", e.target.value)}
                className={inputCls} placeholder="Visa, Mastercard…" />
            </FieldLabel>
            <FieldLabel label="Display Order" note="Lower = appears first">
              <input type="number" min="1" value={form.order} onChange={e => setF("order", e.target.value)}
                className={inputCls} data-testid={`pm-field-order-${code}`} />
            </FieldLabel>
          </div>
        </div>

        <div>
          <SectionLabel>Supported Currencies</SectionLabel>
          <FieldLabel label="" note="Invoice must be in one of these currencies for this method to appear to the client.">
            <CurrencyPicker value={form.supported_currencies} onChange={v => setF("supported_currencies", v)} />
          </FieldLabel>
        </div>

        {/* ── Stripe ── */}
        {code === "stripe" && (
          <div className="space-y-3">
            <SectionLabel>Stripe Integration</SectionLabel>
            <InfoBox
              icon={Icons.Zap}
              color={stripeConfigured && !stripeConfigured?.config_error ? "violet" : stripeConfigured ? "rose" : "rose"}
              title={
                !stripeConfigured
                  ? "Stripe API Not Configured"
                  : "Stripe API Configured"
              }>
              {!stripeConfigured
                ? <>Stripe environment variables are not set. Set <code className="bg-rose-100 px-1 rounded text-xs">STRIPE_SECRET_KEY</code> and redeploy to activate.</>
                : "Stripe credentials are managed through server environment variables. See the API Configuration panel below for the active mode."}
            </InfoBox>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${stripeConfigured ? "bg-emerald-500" : "bg-rose-400"}`} />
                <span className="text-slate-700">API: {stripeConfigured ? "Configured" : "Not configured"}</span>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${stripeConfigured ? "bg-emerald-500" : "bg-slate-300"}`} />
                <span className="text-slate-700">Checkout: {stripeConfigured ? "Available" : "Unavailable"}</span>
              </div>
            </div>
            {/* API Configuration section */}
            {stripeConfigured && (
              <div className="mt-1">
                <SectionLabel>API Configuration</SectionLabel>
                <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 text-sm overflow-hidden">
                  {[
                    { label: "Stripe Credentials", value: stripeConfigured ? "Configured" : "Missing",
                      ok: stripeConfigured },
                    { label: "Stripe Mode",
                      value: method.stripeMode === "live" ? "Live" : method.stripeMode === "test" ? "Test" : "Unknown",
                      ok: method.stripeMode === "live", warn: method.stripeMode === "test" },
                    { label: "Application Environment",
                      value: method.appEnv ? method.appEnv.charAt(0).toUpperCase() + method.appEnv.slice(1) : "Unknown",
                      ok: true },
                    { label: "Checkout Status",
                      value: method.stripeConfigError
                        ? "Configuration Error"
                        : stripeConfigured
                          ? "Ready"
                          : "Not Ready",
                      ok: stripeConfigured && !method.stripeConfigError,
                      error: method.stripeConfigError },
                  ].map(row => (
                    <div key={row.label} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-slate-500">{row.label}</span>
                      <span className={`font-medium ${row.error ? "text-red-600" : row.warn ? "text-amber-600" : row.ok ? "text-emerald-600" : "text-slate-500"}`}>
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
                {method.stripeConfigError && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <Icons.AlertTriangle className="h-3 w-3 shrink-0" />
                    Test credentials detected in Production. Set <code className="bg-red-50 px-1 rounded">STRIPE_SECRET_KEY=sk_live_***</code> and <code className="bg-red-50 px-1 rounded">APP_ENV=production</code> in your Production environment, then redeploy.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── PayPal ── */}
        {code === "paypal" && (
          <div className="space-y-4">
            <SectionLabel>PayPal Integration</SectionLabel>
            <InfoBox icon={Icons.Wallet} color={paypalApiConfigured ? "blue" : "amber"}
              title={paypalApiConfigured ? "PayPal Checkout API Configured" : "API Setup Required"}>
              {paypalApiConfigured
                ? "PayPal Checkout API credentials are configured. Clients can pay via PayPal without providing an email address."
                : <>
                  PayPal API credentials must be configured as environment variables. Required:
                  <ul className="mt-1.5 space-y-0.5 text-xs list-disc ml-4">
                    <li><code className="bg-amber-100 px-1 rounded">PAYPAL_CLIENT_ID</code></li>
                    <li><code className="bg-amber-100 px-1 rounded">PAYPAL_CLIENT_SECRET</code></li>
                    <li><code className="bg-amber-100 px-1 rounded">PAYPAL_MODE</code> (sandbox / live)</li>
                    <li><code className="bg-amber-100 px-1 rounded">PAYPAL_WEBHOOK_ID</code> (recommended)</li>
                  </ul>
                  <p className="mt-1.5">Once configured, enable PayPal to make it available to clients. No PayPal email is needed when API mode is active.</p>
                </>}
            </InfoBox>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${paypalApiConfigured ? "bg-emerald-500" : "bg-rose-400"}`} />
                <span className="text-slate-700">API: {paypalApiConfigured ? "Configured" : "Not configured"}</span>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-slate-300" />
                <span className="text-slate-700">Checkout: API-based when configured</span>
              </div>
            </div>
            {!paypalApiConfigured && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FieldLabel label="PayPal Email / PayPal.me" note="Fallback: shown when API not configured">
                  <input value={form.paypal_email} onChange={e => setF("paypal_email", e.target.value)}
                    className={inputCls} placeholder="payments@yourfirm.com"
                    data-testid="pm-field-paypal-email" />
                </FieldLabel>
                <FieldLabel label="Processing Fee (%)"
                  note="Informational — shown to client. Not automatically deducted.">
                  <div className="flex">
                    <input type="number" step="0.1" min="0" max="15" value={form.fee_pct}
                      onChange={e => setF("fee_pct", e.target.value)}
                      className={`${inputCls} rounded-r-none`} data-testid="pm-field-fee-pct" />
                    <span className="px-3 py-2 text-sm bg-slate-100 border border-l-0 border-slate-200 rounded-r-lg text-slate-500">%</span>
                  </div>
                </FieldLabel>
              </div>
            )}
          </div>
        )}

        {/* ── Wise ── */}
        {code === "wise" && (
          <div className="space-y-4">
            <SectionLabel>Wise Payment Options</SectionLabel>
            <InfoBox icon={Icons.ArrowRightLeft} color="slate" title="External Transfer · Manual Verification">
              Clients pay via Wise using one or more options below. They then submit a Payment Report. Staff verifies and posts via Verify &amp; Post.
            </InfoBox>
            <p className="text-xs text-slate-500">Configure at least one option. All enabled options will be shown to clients.</p>

            {/* Option 1 — Wise Tag */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Icons.AtSign className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm font-semibold text-slate-700">Wise Tag</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-slate-500">{form.wise_details.wise_tag_enabled ? "Enabled" : "Disabled"}</span>
                  <div onClick={() => setF("wise_details.wise_tag_enabled", !form.wise_details.wise_tag_enabled)}
                    className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${form.wise_details.wise_tag_enabled ? "bg-emerald-500" : "bg-slate-300"}`}
                    data-testid="wise-tag-toggle">
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.wise_details.wise_tag_enabled ? "translate-x-4" : ""}`} />
                  </div>
                </label>
              </div>
              <div className="p-4 space-y-3">
                <FieldLabel label="Wise Tag" note="e.g. @yourfirmname">
                  <div className="flex gap-2">
                    <input value={form.wise_details.wise_tag}
                      onChange={e => setF("wise_details.wise_tag", e.target.value)}
                      className={monoCls} placeholder="@taxaccountingnetworkllc"
                      data-testid="wise-tag-input" />
                    {form.wise_details.wise_tag && (
                      <button type="button" onClick={() => navigator.clipboard.writeText(form.wise_details.wise_tag)}
                        className="shrink-0 px-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-500" title="Copy">
                        <Icons.Copy className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </FieldLabel>
                <p className="text-xs text-slate-400">Clients can find and pay the business directly in Wise using this Wise Tag.</p>
              </div>
            </div>

            {/* Option 2 — Wise Payment Link */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Icons.Link className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-semibold text-slate-700">Wise Business Payment Link</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-slate-500">{form.wise_details.payment_link_enabled ? "Enabled" : "Disabled"}</span>
                  <div onClick={() => setF("wise_details.payment_link_enabled", !form.wise_details.payment_link_enabled)}
                    className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${form.wise_details.payment_link_enabled ? "bg-emerald-500" : "bg-slate-300"}`}
                    data-testid="wise-link-toggle">
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.wise_details.payment_link_enabled ? "translate-x-4" : ""}`} />
                  </div>
                </label>
              </div>
              <div className="p-4 space-y-3">
                <FieldLabel label="Wise Business Payment Link (HTTPS)">
                  <div className="flex gap-2">
                    <input value={form.wise_details.payment_link}
                      onChange={e => setF("wise_details.payment_link", e.target.value)}
                      className={inputCls} placeholder="https://wise.com/pay/business/yourfirmname"
                      data-testid="wise-link-input" />
                    {form.wise_details.payment_link?.startsWith("https://") && (
                      <a href={form.wise_details.payment_link} target="_blank" rel="noreferrer"
                        className="shrink-0 px-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-500 flex items-center" title="Open link">
                        <Icons.ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </FieldLabel>
                <p className="text-xs text-slate-400">Clients open this secure Wise payment page to initiate payment. Opens in a new tab — the Tax Flow invoice stays open.</p>
              </div>
            </div>

            {/* Option 3 — Wise QR Code */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Icons.QrCode className="h-4 w-4 text-slate-600" />
                  <span className="text-sm font-semibold text-slate-700">Wise QR Code</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-xs text-slate-500">{form.wise_details.qr_enabled ? "Enabled" : "Disabled"}</span>
                  <div onClick={() => setF("wise_details.qr_enabled", !form.wise_details.qr_enabled)}
                    className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${form.wise_details.qr_enabled ? "bg-emerald-500" : "bg-slate-300"}`}
                    data-testid="wise-qr-toggle">
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.wise_details.qr_enabled ? "translate-x-4" : ""}`} />
                  </div>
                </label>
              </div>
              <div className="p-4 space-y-3">
                <FieldLabel label="Wise QR Code Image" note="Stored securely — served only to authenticated portal clients">
                  <div className="mt-2">
                    <QRUploader value={form.wise_details.qr_document_id} onChange={v => setF("wise_details.qr_document_id", v)} />
                  </div>
                </FieldLabel>
              </div>
            </div>
          </div>
        )}

        {/* ── Thai Bank ── */}
        {code === "thai_bank" && (
          <div className="space-y-4">
            <SectionLabel>Thai Bank Account Details</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldLabel label="Bank Name" required>
                <input value={form.bank_details.bank_name} onChange={e => setF("bank_details.bank_name", e.target.value)}
                  className={inputCls} placeholder="Kasikorn Bank" />
              </FieldLabel>
              <FieldLabel label="Account Name" required>
                <input value={form.bank_details.account_name} onChange={e => setF("bank_details.account_name", e.target.value)}
                  className={inputCls} placeholder="Expat Tax Firm Co., Ltd." />
              </FieldLabel>
              <FieldLabel label="Account Number" required>
                <input value={form.bank_details.account_number} onChange={e => setF("bank_details.account_number", e.target.value)}
                  className={monoCls} placeholder="xxx-x-xxxxx-x" />
              </FieldLabel>
              <FieldLabel label="Phone Number" note="Mobile number for this account (shown to client in bank details)">
                <input value={form.bank_details.phone_number} onChange={e => setF("bank_details.phone_number", e.target.value)}
                  className={monoCls} placeholder="093-579-3833" />
              </FieldLabel>
              <FieldLabel label="Branch">
                <input value={form.bank_details.branch} onChange={e => setF("bank_details.branch", e.target.value)}
                  className={inputCls} placeholder="Silom" />
              </FieldLabel>
              <FieldLabel label="SWIFT / BIC" note="For international senders">
                <input value={form.bank_details.swift_bic} onChange={e => setF("bank_details.swift_bic", e.target.value)}
                  className={monoCls} placeholder="KASITHBK" />
              </FieldLabel>
            </div>
          </div>
        )}

        {/* ── Thai QR ── */}
        {code === "thai_qr" && (
          <div className="space-y-4">
            <SectionLabel>Thai QR Code Configuration</SectionLabel>
            <InfoBox icon={Icons.QrCode} color="slate" title="Static QR — No API">
              Upload an approved PromptPay or bank-issued QR code. The image is stored securely and served only to authenticated portal clients.
            </InfoBox>
            <FieldLabel label="QR Payment Code Image" required>
              <div className="mt-2">
                <QRUploader value={form.qr_document_id} onChange={v => setF("qr_document_id", v)} />
              </div>
            </FieldLabel>
            <FieldLabel label="Account Name" note="Displayed beside the QR code for client confirmation">
              <input value={form.bank_details.account_name} onChange={e => setF("bank_details.account_name", e.target.value)}
                className={inputCls} placeholder="Expat Tax Firm Co., Ltd." />
            </FieldLabel>
          </div>
        )}

        {/* ── Wire ── */}
        {code === "wire" && (
          <div className="space-y-4">
            <SectionLabel>Beneficiary &amp; Receiving Bank</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldLabel label="Beneficiary Name" required>
                <input value={form.bank_details.beneficiary_name} onChange={e => setF("bank_details.beneficiary_name", e.target.value)}
                  className={inputCls} placeholder="Expat Tax Firm Ltd" />
              </FieldLabel>
              <FieldLabel label="Beneficiary Address">
                <input value={form.bank_details.beneficiary_address} onChange={e => setF("bank_details.beneficiary_address", e.target.value)}
                  className={inputCls} placeholder="Bangkok, Thailand" />
              </FieldLabel>
              <FieldLabel label="Bank Name">
                <input value={form.bank_details.bank_name} onChange={e => setF("bank_details.bank_name", e.target.value)}
                  className={inputCls} placeholder="Bangkok Bank" />
              </FieldLabel>
              <FieldLabel label="Bank Address">
                <input value={form.bank_details.bank_address} onChange={e => setF("bank_details.bank_address", e.target.value)}
                  className={inputCls} placeholder="333 Silom Road, Bangkok 10500" />
              </FieldLabel>
              <FieldLabel label="Account Number" required>
                <input value={form.bank_details.account_number} onChange={e => setF("bank_details.account_number", e.target.value)}
                  className={monoCls} placeholder="xxx-x-xxxxx-x" />
              </FieldLabel>
              <FieldLabel label="ABA / Routing Number" note="US banks">
                <input value={form.bank_details.routing_number} onChange={e => setF("bank_details.routing_number", e.target.value)}
                  className={monoCls} placeholder="026073150" />
              </FieldLabel>
              <FieldLabel label="SWIFT / BIC" required>
                <input value={form.bank_details.swift_bic} onChange={e => setF("bank_details.swift_bic", e.target.value)}
                  className={monoCls} placeholder="BKKBTHBK" />
              </FieldLabel>
              <FieldLabel label="IBAN" note="EU / UK accounts">
                <input value={form.bank_details.iban} onChange={e => setF("bank_details.iban", e.target.value)}
                  className={monoCls} placeholder="GB29XXXX…" />
              </FieldLabel>
            </div>
            <SectionLabel>Correspondent / Intermediary Bank (Optional)</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FieldLabel label="Correspondent Bank Name">
                <input value={form.bank_details.correspondent_bank} onChange={e => setF("bank_details.correspondent_bank", e.target.value)}
                  className={inputCls} placeholder="Citibank N.A." />
              </FieldLabel>
              <FieldLabel label="Correspondent SWIFT / BIC">
                <input value={form.bank_details.correspondent_swift} onChange={e => setF("bank_details.correspondent_swift", e.target.value)}
                  className={monoCls} placeholder="CITIUS33" />
              </FieldLabel>
              <FieldLabel label="Additional Wire Instructions" note="Sender fee guidance, special instructions" className="col-span-full">
                <textarea rows={2} value={form.bank_details.wire_instructions} onChange={e => setF("bank_details.wire_instructions", e.target.value)}
                  className={textaCls} placeholder="Please include invoice number in wire details. Sender is responsible for all wire fees." />
              </FieldLabel>
            </div>
          </div>
        )}

        {/* ── Instructions / reference (all non-Stripe) ── */}
        {code !== "stripe" && code !== "paypal" && (
          <div className="space-y-4">
            <SectionLabel>Client Instructions</SectionLabel>
            <FieldLabel label="Payment Instructions" note="Displayed above the structured payment details in the client portal.">
              <textarea rows={3} value={form.instructions} onChange={e => setF("instructions", e.target.value)}
                className={textaCls} placeholder="Please transfer to our account using your invoice number as reference…"
                data-testid={`pm-field-instructions-${code}`} />
            </FieldLabel>
          </div>
        )}
        {code === "paypal" && (
          <div className="space-y-4">
            <SectionLabel>Client Instructions</SectionLabel>
            <FieldLabel label="Instructions" note="Shown to client above PayPal button or payment details.">
              <textarea rows={2} value={form.instructions} onChange={e => setF("instructions", e.target.value)}
                className={textaCls} placeholder="Pay securely via PayPal. Include your invoice number in the payment reference."
                data-testid={`pm-field-instructions-paypal`} />
            </FieldLabel>
          </div>
        )}
        {code !== "stripe" && (
          <FieldLabel label="Payment Reference Format"
            note={`{invoice_number} is replaced with the actual invoice number. Preview: ${(form.payment_reference_format || "INV-{invoice_number}").replace("{invoice_number}", "000123")}`}>
            <input value={form.payment_reference_format} onChange={e => setF("payment_reference_format", e.target.value)}
              className={monoCls} placeholder="INV-{invoice_number}" />
          </FieldLabel>
        )}

        {/* ── Client Preview (P13) ── */}
        {!["stripe", "paypal"].includes(code) && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <SectionLabel>Client Preview</SectionLabel>
              <button type="button" onClick={() => setShowPreview(v => !v)}
                className="text-xs text-[#F5A623] hover:underline mb-2">
                {showPreview ? "Hide" : "Show"} Preview
              </button>
            </div>
            {showPreview && <ClientPreview method={previewMethod} />}
          </div>
        )}
      </div>

      {/* Modal footer */}
      <div className="border-t border-slate-100 px-6 py-4 bg-white shrink-0 flex items-center justify-between">
        <p className="text-xs text-slate-400">
          {method.config_complete
            ? "Configuration complete — use the toggle on the card to enable or disable."
            : "Fill required (*) fields and save to mark this method as ready to enable."}
        </p>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-700 px-3 py-2 rounded-lg hover:bg-slate-100">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="inline-flex items-center gap-2 bg-[#171B2E] hover:bg-[#252B45] text-white
              text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50 transition-colors"
            data-testid={`pm-save-${code}`}>
            {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Save className="h-4 w-4" />}
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Method card (overview grid) ───────────────────────────────────────────────
function MethodCard({ method, stripeConfigured, stripeStatus, paypalApiConfigured, onConfigure, onToggle, toggling }) {
  const code = method.method_code;
  const meta = METHOD_DISPLAY[code] || { label: code, icon: "CreditCard", color: "bg-slate-100 text-slate-700", integration: "Manual", verification: "Manual" };
  const Icon = Icons[meta.icon] || Icons.CreditCard;

  // Stripe-specific mode state
  const stripeMode       = stripeStatus?.stripe_mode || "unconfigured";
  const stripeConfigErr  = stripeStatus?.config_error === true;
  const stripeReady      = stripeStatus?.production_ready === true;

  // Completeness hint (P5)
  const completenessHint = () => {
    if (method.config_complete) return null;
    if (code === "stripe") return !stripeConfigured ? "Stripe API not configured in environment" : null;
    if (code === "paypal") return !paypalApiConfigured
      ? (method.paypal_email ? null : "PayPal API credentials missing — see environment setup") : null;
    if (code === "wise") {
      const wd = method.wise_details || {};
      if (!method.supported_currencies?.length) return "No supported currencies selected";
      const tagOk  = wd.wise_tag_enabled  && (wd.wise_tag || "").trim();
      const linkOk = wd.payment_link_enabled && (wd.payment_link || "").trim();
      const qrOk   = wd.qr_enabled && (wd.qr_document_id || "").trim();
      if (!tagOk && !linkOk && !qrOk)
        return "Configure at least one Wise payment option (Tag, Link, or QR)";
      return null;
    }
    if (code === "thai_bank") {
      const bd = method.bank_details;
      if (!bd?.bank_name) return "Bank name required";
      if (!bd?.account_name) return "Account name required";
      if (!bd?.account_number) return "Account number required";
    }
    if (code === "thai_qr") return !method.qr_document_id ? "QR image must be uploaded" : null;
    if (code === "wire") {
      const bd = method.bank_details;
      if (!bd?.beneficiary_name) return "Beneficiary name required";
      if (!bd?.swift_bic) return "SWIFT / BIC required";
      if (!bd?.account_number) return "Account number required";
    }
    if (!method.supported_currencies?.length) return "No supported currencies selected";
    return "Required fields missing";
  };
  const hint = completenessHint();

  return (
    <div className="bg-white rounded-2xl border border-slate-200 hover:border-slate-300 shadow-sm hover:shadow transition-all overflow-hidden"
      data-testid={`pm-card-${code}`}>
      {/* Card body */}
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            {/* Name + badges */}
            <div className="flex items-center flex-wrap gap-2 mb-1">
              <span className="font-semibold text-slate-900 text-sm">{method.display_name || meta.label}</span>
              {/* Enabled/disabled badge */}
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${method.enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                data-testid={`pm-enabled-badge-${code}`}>
                {method.enabled ? "Enabled" : "Disabled"}
              </span>
              {/* Config status badge */}
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                code === "stripe" && stripeConfigErr
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : method.config_complete
                    ? "bg-teal-50 text-teal-700 border border-teal-200"
                    : "bg-amber-50 text-amber-700 border border-amber-200"
              }`}
                data-testid={`pm-config-badge-${code}`}>
                {code === "stripe" && stripeConfigErr
                  ? "Configuration Error"
                  : method.config_complete ? "Configured" : "Needs Setup"}
              </span>
              {/* Stripe mode badge */}
              {code === "stripe" && stripeConfigured && (
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                  stripeMode === "live"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : stripeMode === "test"
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-slate-50 text-slate-500 border-slate-200"
                }`}
                  data-testid="pm-stripe-mode-badge">
                  {stripeMode === "live" ? "Live Mode" : stripeMode === "test" ? "Test Mode" : "Unknown Mode"}
                </span>
              )}
            </div>

            <p className="text-xs text-slate-500 mb-3 leading-relaxed">{method.subtitle}</p>

            {/* Stripe config error detail */}
            {code === "stripe" && stripeConfigErr && (
              <p className="text-xs text-red-600 mb-2 flex items-center gap-1">
                <Icons.AlertTriangle className="h-3 w-3 shrink-0" />
                Test credentials detected in Production — Credit / Debit Card hidden from clients
              </p>
            )}

            {/* Meta info row */}
            <div className="flex flex-wrap gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Icons.Zap className="h-3 w-3" />{meta.integration}
              </span>
              <span className="flex items-center gap-1">
                <Icons.ShieldCheck className="h-3 w-3" />{meta.verification}
              </span>
              {method.fee_pct > 0 && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Icons.Percent className="h-3 w-3" />{method.fee_pct}% fee
                </span>
              )}
              {/* Stripe: show environment */}
              {code === "stripe" && stripeStatus?.app_env && (
                <span className="flex items-center gap-1 text-slate-400">
                  <Icons.Globe className="h-3 w-3" />
                  Environment: {stripeStatus.app_env}
                </span>
              )}
            </div>

            {/* Currencies */}
            {method.supported_currencies?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2.5">
                {method.supported_currencies.map(c => (
                  <span key={c} className="text-xs bg-slate-50 border border-slate-200 text-slate-500 rounded-md px-1.5 py-0.5">{c}</span>
                ))}
              </div>
            )}

            {/* Wise Payment Options chips */}
            {code === "wise" && (() => {
              const wd = method.wise_details || {};
              const opts = [];
              if (wd.wise_tag_enabled && wd.wise_tag) opts.push("Wise Tag");
              if (wd.payment_link_enabled && wd.payment_link) opts.push("Payment Link");
              if (wd.qr_enabled && wd.qr_document_id) opts.push("QR Code");
              if (!opts.length) return null;
              return (
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="text-xs text-slate-400">Options:</span>
                  {opts.map(o => (
                    <span key={o} className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md px-1.5 py-0.5">{o}</span>
                  ))}
                </div>
              );
            })()}

            {/* Completeness hint */}
            {!method.config_complete && hint && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <Icons.AlertCircle className="h-3 w-3 shrink-0" />{hint}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Card actions */}
      <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/40 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Toggle checked={method.enabled} onChange={() => onToggle(method)} disabled={toggling} />
          <span className="text-xs text-slate-500 select-none">
            {method.enabled ? "Visible to clients" : "Hidden from clients"}
          </span>
        </div>
        <button onClick={() => onConfigure(method)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#171B2E] bg-white hover:bg-slate-100
            border border-slate-200 hover:border-slate-300 rounded-lg px-3 py-1.5 transition-colors"
          data-testid={`pm-configure-${code}`}>
          <Icons.Settings className="h-3.5 w-3.5" />
          {method.config_complete ? "Edit Setup" : "Configure"}
        </button>
      </div>
    </div>
  );
}

// ── Config modal (full-screen on mobile, side drawer on desktop) ──────────────
function ConfigModal({ method, stripeConfigured, stripeStatus, paypalApiConfigured, onSave, onClose }) {
  useEffect(() => {
    const handleKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Enrich method with stripe status for MethodConfigForm rendering
  const enrichedMethod = method.method_code === "stripe"
    ? { ...method,
        stripeMode: stripeStatus?.stripe_mode,
        appEnv: stripeStatus?.app_env,
        stripeConfigError: stripeStatus?.config_error }
    : method;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="pm-config-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full overflow-hidden animate-slide-in-right">
        <MethodConfigForm
          method={enrichedMethod}
          stripeConfigured={stripeConfigured}
          paypalApiConfigured={paypalApiConfigured}
          onSave={onSave}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PaymentMethodsConfig() {
  const { user } = useAuth();
  const [methods, setMethods]                   = useState(null);
  const [loadError, setLoadError]               = useState(null);
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [stripeStatus, setStripeStatus]         = useState(null);  // full stripe_status object
  const [paypalApiConfigured, setPaypalApiConfigured] = useState(false);
  const [editMethod, setEditMethod]             = useState(null);
  const [toggling, setToggling]                 = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const { data } = await api.get("/finance/payment-methods");
      const items = (data.items || []).sort((a, b) => {
        const ai = CANONICAL_ORDER.indexOf(a.method_code);
        const bi = CANONICAL_ORDER.indexOf(b.method_code);
        if (ai === -1 && bi === -1) return (a.order || 99) - (b.order || 99);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      setMethods(items);
      setStripeConfigured(data.stripe_configured || false);
      setStripeStatus(data.stripe_status || null);
      setPaypalApiConfigured(data.paypal_api_configured || false);
    } catch (err) {
      console.error("PaymentMethodsConfig load error:", err);
      setLoadError(err.response?.data?.detail || "Could not load payment methods. Please retry.");
      setMethods([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (method) => {
    const code = method.method_code;
    if (!method.enabled && !method.config_complete) {
      toast.warning("Complete the required configuration before enabling this method");
      setEditMethod(method);
      return;
    }
    if (!method.enabled && code === "stripe" && !stripeConfigured) {
      toast.error("Stripe is not configured in the environment — cannot enable");
      return;
    }
    setToggling(code);
    try {
      await api.post("/finance/payment-methods", { method_code: code, enabled: !method.enabled });
      toast.success(method.enabled ? `${method.display_name} disabled` : `${method.display_name} enabled`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
    setToggling(null);
  };

  const handleSave = () => {
    setEditMethod(null);
    load();
  };

  const activeCount    = (methods || []).filter(m => m.enabled).length;
  const needSetupCount = (methods || []).filter(m => !m.config_complete).length;

  // Stripe mode banner: shown when test credentials are detected in production
  const stripeConfigError = stripeStatus?.config_error === true;
  const stripeMode        = stripeStatus?.stripe_mode || (stripeConfigured ? "test" : "unconfigured");
  const appEnv            = stripeStatus?.app_env || "preview";

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="payment-methods-config-page">

      {/* Header */}
      <FinanceHeader
        title="Payment Methods"
        subtitle="Configure which payment methods are available to clients. A method appears in the client portal only when enabled, configured, and currency-compatible.">
        <div className="flex items-center gap-3">
          {/* Status summary (P2) */}
          {methods !== null && !loadError && (
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2"
              data-testid="pm-status-summary">
              <span className="font-semibold text-slate-700">{methods.length} Methods</span>
              <span className="text-slate-300">·</span>
              <span className="text-emerald-600 font-medium">{activeCount} Active</span>
              {needSetupCount > 0 && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-amber-600 font-medium">{needSetupCount} Need Setup</span>
                </>
              )}
            </div>
          )}
          <button onClick={load}
            className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white"
            data-testid="pm-config-refresh">
            <Icons.RefreshCw className="h-4 w-4" />Refresh
          </button>
        </div>
      </FinanceHeader>

      <FinanceTabs user={user} />

      {/* Stripe production configuration error banner */}
      {stripeConfigError && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
          data-testid="stripe-config-error-banner">
          <Icons.AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-red-800 text-sm">Stripe Configuration Error — Production Environment</p>
            <p className="text-xs text-red-700 mt-0.5">
              Test credentials are configured in a Production environment. Credit / Debit Card is hidden
              from clients until live Stripe credentials are set in <code className="font-mono bg-red-100 px-1 rounded">STRIPE_SECRET_KEY</code>.
            </p>
          </div>
        </div>
      )}

      {/* Loading (P3) */}
      {methods === null && !loadError && (
        <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="pm-loading">
          <Icons.Loader2 className="h-7 w-7 animate-spin text-[#F5A623]" />
          <p className="text-sm text-slate-500">Loading payment methods…</p>
        </div>
      )}

      {/* Error (P3) */}
      {loadError && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="pm-error-state">
          <div className="h-14 w-14 rounded-2xl bg-rose-100 flex items-center justify-center">
            <Icons.WifiOff className="h-7 w-7 text-rose-500" />
          </div>
          <div>
            <p className="font-semibold text-slate-800 text-base">Payment methods could not be loaded</p>
            <p className="text-sm text-slate-500 mt-1 max-w-sm">{loadError}</p>
          </div>
          <button onClick={load}
            className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#171B2E] hover:bg-[#252B45] px-5 py-2.5 rounded-xl"
            data-testid="pm-retry">
            <Icons.RefreshCw className="h-4 w-4" />Retry
          </button>
        </div>
      )}

      {/* Empty / uninitialized (P3) */}
      {methods !== null && !loadError && methods.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="pm-empty-state">
          <div className="h-14 w-14 rounded-2xl bg-slate-100 flex items-center justify-center">
            <Icons.SlidersHorizontal className="h-7 w-7 text-slate-400" />
          </div>
          <div>
            <p className="font-semibold text-slate-800 text-base">Payment methods have not been initialized</p>
            <p className="text-sm text-slate-500 mt-1">The canonical six methods have not been seeded for this firm.</p>
          </div>
          <button onClick={async () => {
            try {
              await api.post("/finance/payment-methods/initialize");
              toast.success("Payment methods initialized");
              load();
            } catch {
              toast.error("Initialization failed — contact support");
            }
          }}
            className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#171B2E] hover:bg-[#252B45] px-5 py-2.5 rounded-xl"
            data-testid="pm-initialize">
            <Icons.Plus className="h-4 w-4" />Initialize Payment Methods
          </button>
        </div>
      )}

      {/* Six method cards grid (P2, P24, P25) */}
      {methods !== null && !loadError && methods.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="pm-cards-grid">
          {methods.map(m => (
            <MethodCard
              key={m.method_code}
              method={m}
              stripeConfigured={stripeConfigured}
              stripeStatus={stripeStatus}
              paypalApiConfigured={paypalApiConfigured}
              onConfigure={setEditMethod}
              onToggle={handleToggle}
              toggling={toggling === m.method_code}
            />
          ))}
        </div>
      )}

      <p className="text-xs text-slate-400 pb-6">
        Changes take effect immediately for new portal sessions.
      </p>

      {/* Configuration modal/drawer (P4) */}
      {editMethod && (
        <ConfigModal
          method={editMethod}
          stripeConfigured={stripeConfigured}
          stripeStatus={stripeStatus}
          paypalApiConfigured={paypalApiConfigured}
          onSave={handleSave}
          onClose={() => setEditMethod(null)}
        />
      )}
    </div>
  );
}
